/**
 * The ONLY path by which message HTML reaches the DOM (web.md §9). The engine already rebuilt the body
 * from an allow-list at ingest, but the stored HTML is still untrusted: it is sanitised again here, with
 * a pinned DOMPurify configuration on a private DOMPurify instance (so no other area's hooks or config
 * can change it), into a DocumentFragment that the article inserts with `replaceChildren`.
 *
 *   - tags = the engine's allow-list; attributes = href src alt width height colspan rowspan data-t;
 *     no other data-* attributes; URIs http(s) only;
 *   - links open in a new tab with rel="noopener noreferrer nofollow";
 *   - images load lazily without a referrer, and are removed when "Show images" is off;
 *   - `p[data-t]` (transcript cue groups) get a leading `[mm:ss]` link to that moment of the original.
 */

import createDOMPurify, { type Config, type DOMPurify } from 'dompurify';
import { timeMark } from './format.js';

/** engine.md §5 "Allow-list rebuild". */
export const ARTICLE_TAGS = [
  'p', 'br', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'ul', 'ol', 'li', 'pre', 'code', 'em', 'strong', 'b', 'i', 'u', 's',
  'sub', 'sup', 'a', 'img', 'figure', 'figcaption', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr',
] as const;

export const ARTICLE_ATTRIBUTES = ['href', 'src', 'alt', 'width', 'height', 'colspan', 'rowspan', 'data-t'] as const;

const HTTP_ONLY = /^https?:/i;

export const PINNED_CONFIG: Config = {
  ALLOWED_TAGS: [...ARTICLE_TAGS],
  ALLOWED_ATTR: [...ARTICLE_ATTRIBUTES],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  ALLOWED_URI_REGEXP: HTTP_ONLY,
  // DOMPurify tests every attribute that is not "URI-safe" against the URI pattern; these carry plain
  // values (numbers, a time in seconds), so they are declared URI-safe — href and src stay checked.
  ADD_URI_SAFE_ATTR: ['width', 'height', 'colspan', 'rowspan', 'data-t'],
  KEEP_CONTENT: true,
  WHOLE_DOCUMENT: false,
};

let purifier: DOMPurify | null = null;

function instance(): DOMPurify {
  if (purifier) return purifier;
  const p = createDOMPurify(window);
  p.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'A') {
      if (node.hasAttribute('href')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer nofollow');
      }
    } else if (node.nodeName === 'IMG') {
      node.setAttribute('loading', 'lazy');
      node.setAttribute('decoding', 'async');
      node.setAttribute('referrerpolicy', 'no-referrer');
    }
  });
  purifier = p;
  return p;
}

export interface ArticleOptions {
  showImages: boolean;
  /** The original's address (for transcript time links); only http(s) is used. */
  url: string | null;
  /** The lead image; inserted after the first paragraph when the body does not already show it. */
  leadImage: string | null;
  leadImageAlt?: string;
}

function safeHttp(url: string | null): URL | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u : null;
  } catch {
    return null;
  }
}

function timeLink(base: URL | null, seconds: number): HTMLElement {
  const label = `[${timeMark(seconds)}]`;
  if (!base) {
    const span = document.createElement('span');
    span.className = 'time-mark';
    span.textContent = label;
    return span;
  }
  const u = new URL(base.href);
  u.searchParams.set('t', `${Math.floor(seconds)}s`);
  const a = document.createElement('a');
  a.className = 'time-mark';
  a.href = u.href;
  a.target = '_blank';
  a.rel = 'noopener noreferrer nofollow';
  a.textContent = label;
  return a;
}

/** Sanitise `bodyHtml` into a fragment ready to insert. Never returns markup that was not allowed. */
export function sanitizeArticle(bodyHtml: string, options: ArticleOptions): DocumentFragment {
  const fragment = instance().sanitize(bodyHtml, { ...PINNED_CONFIG, RETURN_DOM_FRAGMENT: true });

  if (!options.showImages) {
    for (const img of Array.from(fragment.querySelectorAll('img'))) {
      const figure = img.closest('figure');
      (figure ?? img).remove();
    }
  } else {
    const lead = safeHttp(options.leadImage);
    if (lead && !Array.from(fragment.querySelectorAll('img')).some((img) => img.getAttribute('src') === options.leadImage)) {
      const figure = document.createElement('figure');
      const img = document.createElement('img');
      img.src = lead.href;
      img.alt = options.leadImageAlt ?? '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.referrerPolicy = 'no-referrer';
      figure.className = 'lead-image';
      figure.append(img);
      const first = fragment.querySelector('p');
      if (first && first.parentNode === fragment) first.after(figure);
      else fragment.prepend(figure);
    }
  }

  const base = safeHttp(options.url);
  for (const p of Array.from(fragment.querySelectorAll('p[data-t]'))) {
    const seconds = Number(p.getAttribute('data-t'));
    if (!Number.isFinite(seconds)) continue;
    p.classList.add('transcript-cue');
    p.prepend(timeLink(base, seconds), document.createTextNode(' '));
  }
  return fragment;
}
